import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Icon } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { ftuxSignal } from '@/nova/features/ftux/signals';
import type { FtuxTargetId } from '@/nova/features/ftux/targets';
import { useAppSelector } from '@/store';
import { selectCreativeJobsMap } from '@/store/slices/creativeJobs';

import { DotField } from '../../../customizer/components/DotField';
import type { CreativeItem } from '../../creative-api';
import { assetFilename, downloadAsset } from '../../download-asset';
import { useVideoLibrary } from '../../useVideoLibrary';
import { useAssetLibraryStats } from '../../useAssetLibraryStats';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { MOBILE, NAV_DESKTOP, STORYBOARD_COMPOSE } from '@/nova/theme/tokens';
import { isAdvancedMode, videoTabRoutes, type VideoView } from '../../video-routes';
import {
  adPipeline,
  pipelineOf,
  upsertVideoDraft,
  videoCardState,
  videosForPipeline,
  type SavedStoryboard,
  type SavedVideo,
} from '../../video-library-api';
import { DEFAULT_VIDEO_MODEL, SINGLE_CLIP_UNITS, runProgressPct, runUnitMs } from '../../video-models';
import { StoryboardComposer } from '../StoryboardComposer';
import { LibrarySkeletonCards } from '../AdLibraryShell';
import { AssetCardStats, AssetCardStatsSkeleton, AssetStatsSheet } from '../AssetStats';
import { VideoSubjectPicker } from '../VideoSubjectPicker';
import { GamePickerSheet } from '../GamePickerSheet';
import { PoolPickerSheet } from '../PoolPickerSheet';
import { FooterActions, FooterRow, SelCount } from '../GamePickerSheet/GamePickerSheet.styles';
import { lengthForSubjects, promotionFromItem, STORYBOARD_MAX_PROMOTIONS } from '../../storyboard';
import { GamificationPickerSheet } from '../GamificationPickerSheet';
import { LumioMark } from '../LumioMark';
import { openRowUnlessControl } from '../AdLibraryShell/row-open';
import type { VideoSubjectType } from '../../video-subjects';
import { useVideoReopenSubject } from '../../useVideoReopenSubject';
import { revealOnFrame } from '../../reveal-media';
import { artStyleLabel, useActiveArtStyleState } from '../../../art-style/useActiveArtStyle';
import {
  creativeItemFromGame,
  gameTitle,
  storyboardPromotionFromGame,
  type GameSourceRecord,
} from '../../game-sources';
import {
  creativeItemFromFeature,
  storyboardPromotionFromFeature,
  type GamificationFeature,
} from '../../gamification-sources';
import type {
  CreativeJob,
  StoryboardPipeline,
  StoryboardPromotion,
  VideoAspectRatio,
} from '@/store/slices/creativeJobs';
import * as S from './VideoLibrary.styles';

/** Where the FTUX's video ads piece lights Create new (inert outside an FTUX step). */
const FTUX_NEW_VIDEO: FtuxTargetId = 'ads-new-videos';

/** These ads OPEN on the promotion's static key visual (storyboard frame F0), so the very first frame is the
 *  key art, not the ad. Seek the muted poster <video> a little past the middle once its duration is known, so
 *  the listing thumbnail shows the generated video's motion instead of the key art it opens on. */
const POSTER_FRAME_FRACTION = 0.5;
const showRepresentativeFrame = (e: React.SyntheticEvent<HTMLVideoElement>): void => {
  const el = e.currentTarget;
  if (Number.isFinite(el.duration) && el.duration > 0) {
    el.currentTime = Math.min(el.duration * POSTER_FRAME_FRACTION, el.duration - 0.05);
  }
};

/** When a run's `completed` count last advanced (the start of its current unit), for the time-based progress bar. */
type UnitAnchor = { completed: number; at: number };

/** A generating card's progress %: the run's live job (completed / total units) plus its current unit filled over
 *  the model's estimated time, the same math as the editor's bar (see runProgressPct). */
const cardRunProgress = (v: SavedVideo, job: CreativeJob, anchor: UnitAnchor | undefined, now: number): number => {
  const sb = v.storyboard;
  const pipeline = pipelineOf(sb?.pipeline);
  const beats = Array.isArray(sb?.beats) ? sb.beats.length : 0;
  const total =
    job.total && job.total > 0
      ? job.total
      : pipeline === 'single_clip'
        ? SINGLE_CLIP_UNITS
        : Math.max(1, 2 * beats + 1);
  const completed = job.completed ?? 0;
  const inUnitMs = anchor && anchor.completed === completed ? now - anchor.at : 0;
  const model = sb?.video_model || DEFAULT_VIDEO_MODEL;
  return runProgressPct({
    completed,
    total,
    inUnitMs,
    estUnitMs: runUnitMs({ pipeline, beats, total, completed, model }),
  });
};

/** The generating card's progress bar (announced as a progressbar with the card's busy label). */
const CardRunProgressBar = ({ pct, label }: { pct: number; label: string }): React.ReactElement => (
  <S.CardProgressTrack
    role="progressbar"
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(pct)}
  >
    <S.CardProgressFill $pct={pct} />
  </S.CardProgressTrack>
);

/** A stable per-ad seed for the generating dot-field, so simultaneously-generating cards don't drift in sync
 *  (matches how the banner card seeds its field off the banner id). */
const seedFromId = (id: string): number => {
  let s = 0;
  for (let i = 0; i < id.length; i += 1) s = (s + id.charCodeAt(i) * (i + 1)) % 100000;
  return s;
};

/** The storyboard fields a NEW ad carries from its very first write: its tab's pipeline, so the post-mint refresh
 *  already lists it under that tab. A v1 ad keeps its pre-v2 shape (no pipeline key = beats). */
const newAdPipelineFields = (pipeline: StoryboardPipeline): Pick<SavedStoryboard, 'pipeline'> =>
  pipeline === 'single_clip' ? { pipeline } : {};

export interface VideoLibraryProps {
  tenantId?: number;
  brandName: string;
  brandDomain?: string;
  brandLogoUrl?: string;
  /** Promotions whose key art can seed a storyboard (passed through to the editor). */
  items: CreativeItem[];
  /** Whether the banner pool (`items`) has finished loading. Distinct from `items.length===0`: a brand can
   *  genuinely have zero banner placements, and the subject inference must not confuse "not loaded yet" with
   *  "no banners" (that would mislabel a game ad on a bannerless brand). */
  itemsLoaded: boolean;
  beatCost?: number;
  /** Which video tab this library is: `beats` = Video Ads (v1, stitched beats), `single_clip` = Video Ads v2.0
   *  (one clip from a timed shot list). It picks the ads the grid lists, the URLs the library navigates to, and
   *  the pipeline a NEW ad is created with (a reopened ad keeps its own saved pipeline). */
  pipeline: StoryboardPipeline;
  /** The view this library is on, derived by the page from the URL (`/videos`, `/videos/new`, `/videos/:id`)
   *  or reset to the library by a sub-tab click. The library NAVIGATES to enter the editor (it never flips a
   *  boolean), so the URL stays the single source of truth and browser back/forward work. */
  view: VideoView;
  /** Desktop immersive editor active (drives the full-screen canvas). */
  immersed?: boolean;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
}

/**
 * The Video Ads library: a grid of the brand's saved video ads (each openable in the editor, downloadable,
 * deletable) plus a "create new" tile. Opening a saved ad or creating a new one NAVIGATES to `/videos/:id`
 * (or `/videos/new`), which drives the immersive {@link StoryboardComposer}; the layout's back button (wired
 * by the page to navigate back to `/videos`) returns here, and browser back/forward work. Mirrors the Custom
 * studio's list <-> edit shape so the two libraries read as one system.
 */
export const VideoLibrary = ({
  tenantId,
  brandName,
  brandDomain,
  brandLogoUrl,
  items,
  itemsLoaded,
  beatCost,
  pipeline,
  view,
  immersed,
  onBuyCredits,
  onNotify,
}: VideoLibraryProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  // The immersive advanced canvas (React Flow node editor) is desktop-only: unusable below tablet width, so on
  // anything smaller we never render it and hide its toggle. MUST match the page orchestrator's immersive gate
  // (creatives/index.tsx `isDesktop`, same '(min-width: 900px)'): otherwise a mid band could put the composer in
  // advanced mode while the page shell stays non-immersive, rendering the canvas without its full-screen chrome.
  // A `?mode=advanced` deep link (or a desktop->smaller resize) then falls back to the easy modal.
  // The desktop boundary (chrome/immersive canvas appears) — the shared token, kept identical to the page
  // orchestrator's immersive gate (creatives/index.tsx) so the composer's advanced mode and the full-screen
  // shell can never disagree. useMediaQuery strips the '@media' prefix.
  const isDesktop = useMediaQuery(NAV_DESKTOP);
  // On a phone the cards are rows, and a tap anywhere on one opens it (its own buttons keep their actions).
  const rowsOpen = useMediaQuery(MOBILE);
  // This tab's URLs (library, new, an ad by id): Video Ads at /videos, Video Ads v2.0 at /videos-v2.
  const routes = videoTabRoutes(pipeline);
  const { videos, loading, loaded, settled, listFailed, remove, restore, purge, refresh, patch } =
    useVideoLibrary(tenantId);
  // The ads THIS tab lists (the grid + Archived): single-clip ads on Video Ads v2.0, every other ad on Video Ads.
  // Only the listing is filtered; opening an ad by id resolves against the full list, so an ad opened from the other
  // tab's URL still opens (with its own pipeline) instead of reading as missing.
  const tabVideos = React.useMemo(() => videosForPipeline(videos, pipeline), [videos, pipeline]);
  // Subtle per-ad tracking-link stats for the grid (one grouped fetch); an entry exists iff the ad has a tracking link.
  const tabVideoIds = React.useMemo(() => tabVideos.map((v) => v.id), [tabVideos]);
  const {
    byId: miniStats,
    refresh: refreshStats,
    loaded: statsLoaded,
    failed: statsFailed,
  } = useAssetLibraryStats('video', tabVideoIds, { idsLoaded: loaded && !listFailed });
  const viewId = view.kind === 'edit' ? view.id : undefined;
  // Live job status per ad, so a card whose run is in flight shows the generating dot-field overlay (the same
  // realtime channel the banners use). The persistence fix keeps each ad's job_id fresh after a run starts.
  const jobsById = useAppSelector(selectCreativeJobsMap);
  // The brand's primary art style, read once here: a mature one blocks video generation (video models refuse
  // adult content), so the library shows a banner and the composer disables every render. Undefined = not mature.
  // Until the read settles the style is unknown, so the composer keeps renders blocked too (fails closed), but
  // without the banner or the mature note, which only a confirmed mature style shows.
  const { active: activeStyle, loaded: styleLoaded } = useActiveArtStyleState();
  const matureStyleName = activeStyle?.mature
    ? artStyleLabel(activeStyle.name) || t('Your primary art style')
    : undefined;

  // A generating card shows its run's progress bar (cardRunProgress): when each in-flight job's `completed` last
  // advanced, and a clock that ticks only while one of this tab's ads is generating, so the bar fills smoothly.
  const unitAnchorsRef = React.useRef<Record<string, UnitAnchor>>({});
  React.useEffect(() => {
    const anchors = unitAnchorsRef.current;
    for (const j of Object.values(jobsById)) {
      if (j.status !== 'queued' && j.status !== 'running') {
        delete anchors[j.id];
        continue;
      }
      const completed = j.completed ?? 0;
      if (anchors[j.id]?.completed !== completed) anchors[j.id] = { completed, at: Date.now() };
    }
  }, [jobsById]);
  // Only the library grid draws the bars (archived ads have none), so the clock stays off in the editor, where a tick
  // would re-render the composer and its canvas for nothing.
  const anyGenerating = React.useMemo(
    () =>
      view.kind === 'library' &&
      tabVideos.some((v) => {
        if (v.archived) return false;
        const j = v.job_id ? jobsById[v.job_id] : undefined;
        return j?.status === 'queued' || j?.status === 'running';
      }),
    [view.kind, tabVideos, jobsById],
  );
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!anyGenerating) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), STORYBOARD_COMPOSE.PROGRESS_TICK_MS);
    return () => window.clearInterval(id);
  }, [anyGenerating]);

  // The Archived modal: archived ads are pulled OUT of the main grid and reachable via the header count; each
  // offers Restore or a two-step Permanently-delete. `confirmPurgeId` arms the destructive confirm per row.
  const [archivedOpen, setArchivedOpen] = React.useState(false);
  const [confirmPurgeId, setConfirmPurgeId] = React.useState<string | null>(null);
  const { closeHandle: archivedCloseHandle } = useSheetDismiss(() => {
    setArchivedOpen(false);
    setConfirmPurgeId(null);
  });

  // Archiving (the card trash button) is confirmed first: a small sheet explaining it is recoverable from
  // Archived, so it never reads as a permanent delete. `confirmArchiveId` holds the ad awaiting confirmation.
  const [confirmArchiveId, setConfirmArchiveId] = React.useState<string | null>(null);

  // The performance modal for one ad, opened from its card's stats row. Held by id (not a snapshot) so it reads
  // the ad's current versions, and closes on its own if the ad leaves the list.
  // The stats sheet open over the grid is the address's (`?stats=<id>`), so Back from one of its deep links reopens it.
  // Read on the grid only: a composer open over it reads the same key for its own sheet.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsForId = view.kind === 'library' ? statsSheet.value : null;
  const { closeHandle: archiveConfirmCloseHandle, dismiss: dismissArchiveConfirm } = useSheetDismiss(() =>
    setConfirmArchiveId(null),
  );

  // `new`: mint the draft entity on mount (draft-first, as before), then swap the URL to its id WITHOUT
  // navigating: navigate() would push a second history entry for the same ad and re-run the URL sync; replaceState
  // keeps this instance and React Router's history state, so back/forward still work
  // and a reload reopens this draft instead of minting another. Ref-guarded so a re-run (tenant resolving,
  // StrictMode) never creates twice.
  const [created, setCreated] = React.useState<SavedVideo | null>(null);
  const createdRef = React.useRef(false);
  // Per-attempt generation for the async mint: bumped on every mint start AND on reset, so a stale draft-create
  // `.then` (owner cancelled then started a different ad) can tell it is no longer the current attempt and bail,
  // which a boolean createdRef cannot (a restart would re-arm it to true).
  const createGenRef = React.useRef(0);
  // The chosen "what to advertise" category for a NEW ad. Until it is picked the new-ad flow shows the
  // category chooser instead of minting a draft; only Promotions proceeds to the composer in v1 (Games /
  // Gamification are build-ahead and open a coming-soon note).
  const [subject, setSubject] = React.useState<VideoSubjectType | null>(null);
  // For a GAME or GAMIFICATION ad, the chosen subjects become the composer's pool (mapped to CreativeItem)
  // so the storyboard flow is reused unchanged. Null for a promotion ad (the composer gets the banner pool).
  const [subjectItems, setSubjectItems] = React.useState<CreativeItem[] | null>(null);
  // A NEW promotion ad's picks in the promotions picker (step 2), in pick order; the ad is minted on Continue.
  const [newPromoIds, setNewPromoIds] = React.useState<string[]>([]);

  // VideoLibrary does NOT remount across /videos <-> /videos/new <-> /videos/:id: it lives inside the asset
  // tab's <SwitchFade>, which is keyed by the tab, not the pathname. So a fresh "Create new" must reset the
  // new-ad session; otherwise the previous category + minted draft persist and the composer reopens that
  // (empty) draft instead of showing the chooser. The library is the one state with no draft mint, so
  // resetting here is race-free. (`createdRef` re-arms the mint for the next ad.)
  React.useEffect(() => {
    if (view.kind !== 'library') return;
    createdRef.current = false;
    createGenRef.current += 1; // invalidate any in-flight mint .then
    setCreated(null);
    setSubject(null);
    setSubjectItems(null);
    setNewPromoIds([]);
  }, [view.kind]);

  // `edit`: resolved from the library list (there is no single-entity read). Only a SETTLED list for this
  // tenant may declare the id missing; then leave to the library with a notice.
  React.useEffect(() => {
    if (view.kind !== 'edit' || !viewId || !loaded || loading) return;
    // The asset minted here moments ago is not missing while the list catches up: opening its stats or links sheet moves
    // the address from /new to its id before the post-mint refresh has landed it.
    if (created && created.tenant_id === String(tenantId) && created.id === viewId) return;
    if (!videos.some((v) => v.id === viewId)) {
      onNotify(t("That video ad couldn't be found."));
      navigate(routes.library(), { replace: true });
    }
  }, [view.kind, viewId, loaded, loading, videos, created, tenantId, routes, onNotify, navigate, t]);

  const startNew = React.useCallback((): void => {
    ftuxSignal('ads-new-pressed'); // FTUX: the video ads piece hands the library over (a no-op outside it)
    navigate(routes.create());
  }, [navigate, routes]);
  const openSaved = React.useCallback((v: SavedVideo): void => navigate(routes.edit(v.id)), [navigate, routes]);

  // Games / Gamification choose their subjects in a picker, so (unlike a promotion ad) the draft is minted
  // on CONFIRM, not by the new-ad effect: seed the storyboard with the chosen subjects as promotions and
  // hand them to the composer as its pool (`subjectItems`). Mirrors the promotion mint's draft-first + swap.
  const beginSeededAd = React.useCallback(
    (
      builtItems: CreativeItem[],
      promotions: StoryboardPromotion[],
      name: string,
      subjectType: VideoSubjectType,
    ): void => {
      if (!tenantId || builtItems.length === 0 || createdRef.current) return;
      createdRef.current = true;
      const gen = ++createGenRef.current;
      const storyboard: SavedStoryboard = {
        aspect_ratio: '16:9' as VideoAspectRatio,
        subject_type: subjectType,
        brand: {},
        promotions,
        beats: [],
        ...newAdPipelineFields(pipeline),
        // A beats ad seeded with several subjects opens at the shortest length that fits them: the default 10s fits
        // one, and composing more at 10s overran the length or dropped subjects.
        ...(pipeline !== 'single_clip' && lengthForSubjects(promotions.length)
          ? { target_duration_seconds: lengthForSubjects(promotions.length) }
          : {}),
      };
      setSubjectItems(builtItems);
      void upsertVideoDraft(tenantId, { name, status: 'draft', image_url: promotions[0]?.image_url, storyboard }).then(
        (id) => {
          // Bail if this attempt is no longer current BEFORE touching createdRef / any side effect, so a stale
          // failed mint can't release a newer attempt's re-entry lock (which would allow a duplicate draft).
          if (gen !== createGenRef.current) return;
          if (!id) {
            createdRef.current = false;
            onNotify(t("That couldn't be created. Please try again."));
            return;
          }
          setCreated({
            id,
            tenant_id: String(tenantId),
            name,
            media_type: 'video',
            video_url: '',
            video_versions: [],
            image_url: promotions[0]?.image_url ?? '',
            status: 'draft',
            archived: false,
            storyboard,
          });
          window.history.replaceState(window.history.state, '', routes.edit(id));
          refresh();
        },
      );
    },
    [tenantId, pipeline, routes, t, onNotify, refresh],
  );

  const handleGamesChosen = React.useCallback(
    (games: GameSourceRecord[]): void => {
      const aspect: VideoAspectRatio = '16:9';
      beginSeededAd(
        games.map((g) => creativeItemFromGame(g, aspect)),
        games.map((g) => storyboardPromotionFromGame(g, aspect)),
        games.length === 1 ? gameTitle(games[0]) : t('Game ad'),
        'game',
      );
    },
    [beginSeededAd, t],
  );

  // A promotion ad: the owner picks its promotions first (nothing preselected), then the draft is minted with
  // them. The composer keeps the WHOLE banner pool (so promotions can still be changed in the editor).
  const handlePromotionsChosen = React.useCallback((): void => {
    const picked = newPromoIds
      .map((id) => items.find((i) => i.banner_id === id))
      .filter((i): i is CreativeItem => Boolean(i));
    if (picked.length === 0) return;
    beginSeededAd(items, picked.map(promotionFromItem), 'Untitled video ad', 'promotion');
    setSubjectItems(null); // promotions keep the full pool as the composer's items
  }, [newPromoIds, items, beginSeededAd]);
  const toggleNewPromo = React.useCallback(
    (id: string): void =>
      setNewPromoIds((cur) =>
        cur.includes(id)
          ? cur.filter((x) => x !== id)
          : pipeline === 'single_clip'
            ? [id] // a single-clip ad advertises one subject
            : cur.length < STORYBOARD_MAX_PROMOTIONS
              ? [...cur, id]
              : cur,
      ),
    [pipeline],
  );

  const handleFeaturesChosen = React.useCallback(
    (features: GamificationFeature[]): void => {
      beginSeededAd(
        features.map(creativeItemFromFeature),
        features.map(storyboardPromotionFromFeature),
        features.length === 1 ? features[0].name : t('Gamification ad'),
        'gamification',
      );
    },
    [beginSeededAd, t],
  );

  const download = React.useCallback(
    (v: SavedVideo): void => {
      void downloadAsset(v.video_url, assetFilename(brandName, 'video', v.video_url, 'mp4'));
    },
    [brandName],
  );

  // The saved ad the editor reopens: the freshly-minted draft for `new`, else the one matching the :id. The
  // list can still be loading on a cold deep link, so hold until it (or the just-created entity) is available;
  // the `edit` effect above redirects to the library if a settled list has no such id.
  const editing = view.kind !== 'library';
  // Re-read the grid's affiliate stats when the editor CLOSES (editing true -> false). The owner may have just
  // generated a tracking link in there, which mints a campaign the stats hook fetched before it existed; without
  // this the card stays "Stats not available" until a full page refresh. Refetch on close catches it (and any
  // fresh activity) with one grouped read. Guarded on the transition so it fires once, not on every render.
  const wasEditingRef = React.useRef(editing);
  React.useEffect(() => {
    if (wasEditingRef.current && !editing) refreshStats();
    wasEditingRef.current = editing;
  }, [editing, refreshStats]);
  // The freshly minted draft counts only for the brand it was made for: right after a brand switch it must not stand
  // in for the (still empty) new brand's list, or its editor would render, and save, under the new brand.
  const createdHere = created && created.tenant_id === String(tenantId) ? created : null;
  const reopen: SavedVideo | undefined = editing
    ? view.kind === 'new'
      ? // Prefer the LIVE entity from the list (it reflects `patch` optimistic updates + refetches, so the
        // composer's version picker populates) once the post-create refresh() has landed it; fall back to the
        // frozen `created` snapshot only for the brief window before then.
        createdHere
        ? videos.find((v) => v.id === createdHere.id) ?? createdHere
        : undefined
      : viewId
        ? createdHere?.id === viewId
          ? videos.find((v) => v.id === viewId) ?? createdHere
          : videos.find((v) => v.id === viewId)
        : undefined
    : undefined;

  // What the ad advertises and the composer's pool (a reopened ad's subject is inferred; see useVideoReopenSubject).
  const {
    subjectType: effectiveSubject,
    composerItems,
    settled: subjectSettled,
  } = useVideoReopenSubject({ reopen, subject, subjectItems, items, itemsLoaded });

  // The pipeline the editor runs: a NEW ad takes this tab's; a reopened ad keeps its own saved one (so an ad opened
  // from the other tab's URL is still edited as what it is). Until a cold deep link's ad loads it reads as beats.
  const composerPipeline = adPipeline(reopen?.storyboard?.pipeline, pipeline, view.kind === 'new');
  // The advanced canvas (v1 beats only) exists on the Video Ads tab and for a beats ad. MUST match the page's
  // immersive gate (creatives/index.tsx, which keys on the TAB + `?mode=advanced`): a single-clip ad reached with
  // `?mode=advanced` on this tab drops the mode (effect below) so the shell and the easy composer agree, and on
  // Video Ads v2.0 the mode is simply ignored (that tab is never immersive).
  const canvasAllowed = pipeline === 'beats' && composerPipeline === 'beats';
  // `?mode=advanced` picks the immersive canvas; absent (or below desktop) = the default easy modal (both at /videos/:id).
  const advanced = canvasAllowed && isAdvancedMode(search) && isDesktop;
  const reopenId = reopen?.id;
  React.useEffect(() => {
    if (pipeline !== 'beats' || composerPipeline !== 'single_clip' || view.kind !== 'edit' || !reopenId) return;
    if (isAdvancedMode(search)) navigate(routes.edit(reopenId), { replace: true });
  }, [pipeline, composerPipeline, view.kind, reopenId, search, routes, navigate]);

  const goToLibrary = (): void => navigate(routes.library());
  // The mode toggle: easy -> advanced adds `?mode=advanced`; advanced -> easy drops it. Same id either way.
  const toggleMode = (): void => {
    if (!reopen) return;
    navigate(advanced ? routes.edit(reopen.id) : { pathname: routes.edit(reopen.id), search: '?mode=advanced' });
  };

  // The composer mounts only once a reopened ad's subject is settled (useVideoReopenSubject).
  const composerNode =
    editing && reopen && subjectSettled ? (
      <StoryboardComposer
        // Keyed by brand and ad: a brand switch remounts it, so it never renders (or saves) one brand's ad under another.
        key={`${tenantId}:${reopen.id}`}
        tenantId={tenantId}
        brandName={brandName}
        brandDomain={brandDomain}
        brandLogoUrl={brandLogoUrl}
        items={composerItems}
        subjectType={effectiveSubject}
        pipeline={composerPipeline}
        beatCost={beatCost}
        mode={advanced ? 'advanced' : 'easy'}
        immersed={immersed}
        reopen={reopen}
        onClose={goToLibrary}
        onToggleMode={isDesktop && canvasAllowed ? toggleMode : undefined}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
        onEntityPatch={patch}
        matureStyle={matureStyleName !== undefined}
        styleChecking={!styleLoaded}
      />
    ) : null;

  // ADVANCED editor: the immersive canvas takes the whole surface (no library grid behind it). Only for an
  // EXISTING ad — a NEW ad opens easy (chooser -> composer) and never lands here before it has an id.
  if (editing && advanced && view.kind === 'edit') {
    // composerNode is the element when `editing && reopen` (both true here once reopen lands), else null.
    return composerNode ?? <S.EmptyNote text="Loading your video ads…" />;
  }

  // The grid shows only this tab's LIVE (non-archived) ads; archived ones live behind the header count in the modal.
  const liveVideos = tabVideos.filter((v) => !v.archived);
  const archivedVideos = tabVideos.filter((v) => v.archived);
  const statsForVideo = statsForId ? liveVideos.find((v) => v.id === statsForId) : undefined;

  return (
    <div>
      <S.Intro>
        <S.IntroHead>
          {pipeline === 'single_clip' ? (
            <S.Title text="Your single-clip video ads" />
          ) : (
            <S.Title text="Your video ads" />
          )}
          {settled && archivedVideos.length > 0 ? (
            <S.ArchivedLink type="button" onClick={() => setArchivedOpen(true)}>
              <Icon name="trash" size={13} />
              <S.ArchivedLinkText text="Archived video ads ({{n}})" placeholders={{ n: archivedVideos.length }} />
            </S.ArchivedLink>
          ) : null}
        </S.IntroHead>
        {/* Two static <Text> lines (not a ternary in the prop) so both copies are i18n-extractable. */}
        {pipeline === 'single_clip' ? (
          <S.Body text="The single-clip generator: each ad is one continuous clip made from a timed shot list. Open one to edit or regenerate it, download or delete it, or create a new one." />
        ) : (
          <S.Body text="Every video ad you have generated. Open one to edit or regenerate it, download or delete it, or create a new one." />
        )}
      </S.Intro>
      {matureStyleName !== undefined ? (
        <S.MatureNotice
          tone="warning"
          title="Video generation is off for this art style"
          text="{{style}} is flagged as mature content, which video generation models don't support. Choose a different primary art style to generate video ads."
          placeholders={{ style: matureStyleName }}
        />
      ) : null}
      {/* Until the brand's list has landed: skeleton cards, never an empty grid that fills in. */}
      <S.SavedGrid>
        <S.NewTile type="button" onClick={startNew} aria-label={t('Create a new video ad')} data-ftux={FTUX_NEW_VIDEO}>
          <Icon name="plus-circle" size={26} />
          <S.NewTileText text="Create new" />
        </S.NewTile>
        {settled ? null : (
          <LibrarySkeletonCards label={t('Loading your video ads…')} footer={<AssetCardStatsSkeleton />} />
        )}
        {(settled ? liveVideos : []).map((v) => {
          // Three glanceable states: a rendered video, a composed-but-unrendered storyboard (beats or a shot plan),
          // or a bare draft.
          const state = videoCardState(v);
          const genJob = v.job_id ? jobsById[v.job_id] : undefined;
          const generating = genJob?.status === 'queued' || genJob?.status === 'running';
          return (
            <S.SavedCard
              key={v.id}
              $reveal
              $opens={rowsOpen}
              onClick={rowsOpen ? (event) => openRowUnlessControl(event, () => openSaved(v)) : undefined}
            >
              <S.Poster type="button" onClick={() => openSaved(v)} aria-label={t('Open this video ad')}>
                {generating ? null : (
                  <S.StatusPill>
                    <S.StatusDot $tone={state} aria-hidden="true" />
                    <S.StatusPillText
                      text={state === 'video' ? 'Video' : state === 'storyboard' ? 'Storyboard' : 'Draft'}
                    />
                  </S.StatusPill>
                )}
                {state === 'video' ? (
                  <S.PosterVideo
                    key={v.video_url}
                    {...revealOnFrame}
                    src={v.video_url}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={showRepresentativeFrame}
                  />
                ) : state === 'storyboard' ? (
                  <S.PosterStoryboard>
                    {/* Lumio brand mark for this AI feature (never an approximated sparkle/star). */}
                    <LumioMark size={22} />
                    <S.PosterStoryboardText text="Storyboard ready" />
                  </S.PosterStoryboard>
                ) : (
                  <S.PosterEmpty text="Not generated yet" />
                )}
                {generating ? (
                  <S.BusyOverlay aria-live="polite">
                    <DotField seed={seedFromId(v.id)} />
                    <S.BusyInner>
                      <S.BusySpark src="/assets/acq/lumio-spark.svg" alt="" aria-hidden="true" />
                      <S.BusyText text="Generating…" />
                    </S.BusyInner>
                    {genJob ? (
                      <CardRunProgressBar
                        pct={cardRunProgress(v, genJob, unitAnchorsRef.current[genJob.id], now)}
                        label={t('Generating…')}
                      />
                    ) : null}
                  </S.BusyOverlay>
                ) : null}
              </S.Poster>
              <S.SavedBody>
                <S.SavedName text={v.name || t('Untitled video ad')} />
                <S.SavedFoot>
                  <S.SavedMeta text={v.storyboard?.aspect_ratio || t('Video ad')} />
                  <S.SavedActions>
                    <S.SavedBtn type="button" onClick={() => openSaved(v)} aria-label={t('Edit')}>
                      <Icon name="edit" size={14} />
                    </S.SavedBtn>
                    <S.SavedBtn
                      type="button"
                      onClick={() => download(v)}
                      aria-label={t('Download')}
                      disabled={!v.video_url}
                    >
                      <Icon name="download" size={14} />
                    </S.SavedBtn>
                    <S.SavedBtn type="button" onClick={() => setConfirmArchiveId(v.id)} aria-label={t('Archive')}>
                      <Icon name="trash" size={14} />
                    </S.SavedBtn>
                  </S.SavedActions>
                </S.SavedFoot>
                {/* Bottom stats: the ad's tracking-link figures (opening its stats sheet), a hint while it has no link,
                    a skeleton until the grid's read settles for this brand (never a false "no link"). */}
                <AssetCardStats
                  loaded={statsLoaded}
                  failed={statsFailed}
                  stats={miniStats[v.id]}
                  hint={v.video_url ? 'get-link' : 'render-video'}
                  onOpen={() => statsSheet.open(v.id)}
                />
              </S.SavedBody>
            </S.SavedCard>
          );
        })}
      </S.SavedGrid>
      {settled && liveVideos.length === 0 ? (
        listFailed ? (
          <S.EmptyNote text="Your video ads couldn't be loaded. Reload the page to try again." />
        ) : pipeline === 'single_clip' ? (
          <S.EmptyNote text="No single-clip video ads yet. Create your first one." />
        ) : (
          <S.EmptyNote text="No video ads yet. Create your first one." />
        )
      ) : null}

      {/* Easy editor: a modal over the grid (advanced returns early above as the immersive canvas). */}
      {composerNode}

      {statsForVideo ? (
        <AssetStatsSheet
          kind="video"
          creativeId={statsForVideo.id}
          name={statsForVideo.name}
          onClose={() => statsSheet.close()}
        />
      ) : null}

      {/* Step 1 of a NEW ad: pick what to advertise, then that category's item picker. */}
      {view.kind === 'new' && subject === null ? (
        <VideoSubjectPicker onPick={setSubject} onClose={goToLibrary} />
      ) : null}

      {view.kind === 'new' && subject === 'promotion' && !created ? (
        <PoolPickerSheet
          title={t('Choose the promotions this ad advertises')}
          poolItems={items}
          selectedIds={newPromoIds}
          labelOf={(i) => i.headline || i.internal_name || i.banner_id}
          onToggle={toggleNewPromo}
          onClose={goToLibrary}
          max={pipeline === 'single_clip' ? 1 : STORYBOARD_MAX_PROMOTIONS}
          emptyText="This brand has no promotions yet. Create one in All Promotions & Creatives first."
          hint="Pick one or more. The first is the ad's key visual; the AI arranges them into beats with smooth transitions. Reorder them on the timeline."
          footer={
            <FooterRow>
              <SelCount text="{{n}} selected" placeholders={{ n: newPromoIds.length }} />
              <FooterActions>
                <Button variant="text" label={t('Cancel')} onClick={goToLibrary} />
                <Button
                  variant="primary"
                  label={t('Continue')}
                  onClick={handlePromotionsChosen}
                  disabled={newPromoIds.length === 0}
                />
              </FooterActions>
            </FooterRow>
          }
        />
      ) : null}

      {view.kind === 'new' && subject === 'game' && !created ? (
        <GamePickerSheet onConfirm={handleGamesChosen} onClose={goToLibrary} />
      ) : null}

      {view.kind === 'new' && subject === 'gamification' && !created ? (
        <GamificationPickerSheet onConfirm={handleFeaturesChosen} onClose={goToLibrary} />
      ) : null}

      {confirmArchiveId ? (
        <Sheet
          title="Archive this video ad?"
          onClose={() => setConfirmArchiveId(null)}
          closeHandle={archiveConfirmCloseHandle}
          maxWidth="460px"
          footer={
            <S.ConfirmFoot>
              <Button variant="text" label={t('Cancel')} onClick={dismissArchiveConfirm} />
              <Button
                variant="primary"
                label={t('Archive')}
                onClick={() => {
                  const id = confirmArchiveId;
                  dismissArchiveConfirm();
                  void remove(id);
                }}
              />
            </S.ConfirmFoot>
          }
        >
          <S.ConfirmBody text="It will be moved to Archived video ads, where you can restore it later or permanently delete it. It will not show in your library." />
        </Sheet>
      ) : null}

      {archivedOpen ? (
        <Sheet
          title="Archived video ads"
          onClose={() => {
            setArchivedOpen(false);
            setConfirmPurgeId(null);
          }}
          closeHandle={archivedCloseHandle}
          maxWidth="560px"
        >
          <S.ArchivedList>
            {archivedVideos.length === 0 ? (
              <S.EmptyNote text="No archived video ads." />
            ) : (
              archivedVideos.map((v) => (
                <S.ArchivedRow key={v.id}>
                  <S.ArchivedThumb>
                    {v.video_url ? (
                      <S.ArchivedThumbVideo
                        src={v.video_url}
                        muted
                        preload="metadata"
                        onLoadedMetadata={showRepresentativeFrame}
                      />
                    ) : null}
                  </S.ArchivedThumb>
                  <S.ArchivedInfo>
                    <S.ArchivedName text={v.name || t('Untitled video ad')} />
                    <S.SavedMeta text={v.storyboard?.aspect_ratio || t('Video ad')} />
                  </S.ArchivedInfo>
                  {confirmPurgeId === v.id ? (
                    <S.ArchivedActions>
                      <S.ArchivedConfirmText text="Delete forever?" />
                      <Button variant="text" label={t('Cancel')} onClick={() => setConfirmPurgeId(null)} />
                      <S.DangerBtn
                        type="button"
                        onClick={() => {
                          setConfirmPurgeId(null);
                          void purge(v.id);
                        }}
                      >
                        <Icon name="trash" size={14} />
                        <S.DangerBtnText text="Delete" />
                      </S.DangerBtn>
                    </S.ArchivedActions>
                  ) : (
                    <S.ArchivedActions>
                      <Button variant="outline" label={t('Restore')} onClick={() => void restore(v.id)} />
                      <S.ArchivedDeleteBtn
                        type="button"
                        onClick={() => setConfirmPurgeId(v.id)}
                        aria-label={t('Delete permanently')}
                      >
                        <Icon name="trash" size={15} />
                      </S.ArchivedDeleteBtn>
                    </S.ArchivedActions>
                  )}
                </S.ArchivedRow>
              ))
            )}
          </S.ArchivedList>
        </Sheet>
      ) : null}
    </div>
  );
};

export default VideoLibrary;
